/**
 * TideChart Component
 *
 * This component creates an interactive tide prediction visualization that displays
 * tide height data over time with clear formatting and EST timezone.
 *
 * Key Features:
 * - Interactive zooming and panning
 * - EST timezone display
 * - Clear tide height visualization
 * - Responsive design
 * - Fullscreen mode
 *
 * @component
 * @param {Array} tideData - Array of tide prediction data points
 */

import React, { useState, useRef, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { DateTime } from "luxon";
import "chart.js/auto";
import {
  Box,
  IconButton,
  Modal,
  Paper,
  Typography,
  Slider,
  Button,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

// Register all required Chart.js components and plugins
ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

// Note: Chart.js font defaults are set globally in WeatherChart.js

/**
 * Main TideChart Component
 *
 * This component renders the tide prediction visualization with interactive features.
 *
 * @param {Object} props
 * @param {Object} props.tideData - Tide prediction data object
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @returns {JSX.Element} Tide prediction visualization
 */
const TideChart = ({ tideData, loading, error }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dateRange, setDateRange] = useState([0, 2]); // [start, end] in days from start
  const [viewRange, setViewRange] = useState(2); // 2 days view
  const chartRef = useRef(null);

  // Calculate scroll range based on data
  const totalDays = tideData?.data
    ? (new Date(tideData.data[tideData.data.length - 1].PredictionTime) -
        new Date(tideData.data[0].PredictionTime)) /
      (1000 * 60 * 60 * 24)
    : 0;
  const maxEndValue = Math.max(viewRange, totalDays);

  // Handle range change
  const handleRangeChange = (event, newValue) => {
    const [start, end] = newValue;
    const range = end - start;

    // Ensure minimum 1 day range
    if (range < 1) {
      if (start === dateRange[0]) {
        setDateRange([start, start + 1]);
      } else {
        setDateRange([end - 1, end]);
      }
    } else {
      setDateRange([start, end]);
    }
  };

  // Handle navigation buttons
  const handleScrollLeft = () => {
    const newStart = Math.max(0, dateRange[0] - 1);
    const newEnd = Math.min(maxEndValue, dateRange[1] - 1);
    setDateRange([newStart, newEnd]);
  };

  const handleScrollRight = () => {
    const newStart = Math.max(0, dateRange[0] + 1);
    const newEnd = Math.min(maxEndValue, dateRange[1] + 1);
    setDateRange([newStart, newEnd]);
  };

  // Calculate visible range based on date range
  const startDate = tideData?.data
    ? new Date(tideData.data[0].PredictionTime)
    : new Date();
  const visibleStart = new Date(
    startDate.getTime() + dateRange[0] * 24 * 60 * 60 * 1000
  );
  const visibleEnd = new Date(
    startDate.getTime() + dateRange[1] * 24 * 60 * 60 * 1000
  );

  // Sync chart view with scrollbar
  useEffect(() => {
    if (chartRef.current && chartRef.current.chartInstance) {
      const chart = chartRef.current.chartInstance;
      chart.update("none"); // Update without animation
    }
  }, [dateRange]);

  // Helper function to format date for scrollbar
  const formatScrollbarDate = (date) => {
    return DateTime.fromJSDate(date).toFormat("MMM dd, HH:mm");
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading tide predictions...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!tideData || !tideData.data || tideData.data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "300px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>No tide prediction data available.</Typography>
      </Box>
    );
  }

  // Process tide data
  const timestamps = tideData.data.map((entry) =>
    DateTime.fromISO(entry.PredictionTime).toJSDate()
  );

  const tideValues = tideData.data.map((entry) => parseFloat(entry.value));
  const tideValuesFeet = tideValues.map((value) => value * 3.28084); // Convert meters to feet

  // Get current time in UTC
  const now = new Date();
  const nowMillis = now.getTime();

  // Find the index of the first future point (>= now)
  const firstFutureIndex = timestamps.findIndex(
    (t) => t.getTime() >= nowMillis
  );

  // Filter future tide data only
  const futureTideValues = tideValues.slice(firstFutureIndex);
  const futureTideValuesFeet = tideValuesFeet.slice(firstFutureIndex);
  const futureTimestamps = timestamps.slice(firstFutureIndex);

  /**
   * Find the next peak (high tide) and trough (low tide) using local extrema detection
   * This finds the immediate next high/low tide, not the absolute maximum/minimum
   */
  const findNextPeakAndTrough = (values, startIndex) => {
    if (values.length < 3) return { peakIdx: null, troughIdx: null };

    let nextPeakIdx = null;
    let nextTroughIdx = null;

    // Start searching from the future data (after startIndex)
    for (let i = startIndex + 1; i < tideValues.length - 1; i++) {
      const prev = tideValues[i - 1];
      const curr = tideValues[i];
      const next = tideValues[i + 1];

      // Check for local maximum (peak/high tide)
      if (nextPeakIdx === null && curr > prev && curr > next) {
        nextPeakIdx = i;
      }

      // Check for local minimum (trough/low tide)
      if (nextTroughIdx === null && curr < prev && curr < next) {
        nextTroughIdx = i;
      }

      // Stop if we found both
      if (nextPeakIdx !== null && nextTroughIdx !== null) {
        break;
      }
    }

    return { peakIdx: nextPeakIdx, troughIdx: nextTroughIdx };
  };

  const { peakIdx, troughIdx } = findNextPeakAndTrough(
    tideValues,
    firstFutureIndex
  );

  // Get the values for next high and low tide
  const nextHighestIndex = peakIdx;
  const nextLowestIndex = troughIdx;
  const nextMaxTide =
    nextHighestIndex !== null ? tideValues[nextHighestIndex] : null;
  const nextMinTide =
    nextLowestIndex !== null ? tideValues[nextLowestIndex] : null;
  const nextMaxTideFeet =
    nextHighestIndex !== null ? tideValuesFeet[nextHighestIndex] : null;
  const nextMinTideFeet =
    nextLowestIndex !== null ? tideValuesFeet[nextLowestIndex] : null;

  // Find min and max for better chart scaling (using all data for scaling)
  const minTide = Math.min(...tideValues);
  const maxTide = Math.max(...tideValues);
  const minTideFeet = Math.min(...tideValuesFeet);
  const maxTideFeet = Math.max(...tideValuesFeet);
  const tideRange = maxTide - minTide;
  const tideRangeFeet = maxTideFeet - minTideFeet;

  // Helper to format time with timezone
  const formatTime = (isoString) =>
    DateTime.fromISO(isoString).toFormat("MMM dd, yyyy HH:mm ZZZZ");

  // Prepare chart data: past (gray), future (blue) with highlighted points and water shade
  const chartData = {
    labels: timestamps,
    datasets: [
      // Past data (gray) - feet
      {
        label: "Past Tide Height (ft)",
        data: timestamps.map((_, i) =>
          i < firstFutureIndex ? tideValuesFeet[i] : null
        ),
        borderColor: "#bdbdbd",
        backgroundColor: "rgba(189, 189, 189, 0.1)",
        borderWidth: 2,
        fill: false,
        tension: 0.4,
        yAxisID: "y",
        pointRadius: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0; // No points for past data
          if (i === nextHighestIndex || i === nextLowestIndex) return 7;
          return 2;
        }),
        pointHoverRadius: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0;
          if (i === nextHighestIndex || i === nextLowestIndex) return 10;
          return 6;
        }),
        pointBackgroundColor: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return "transparent";
          if (i === nextHighestIndex) return "#43a047"; // green for highest
          if (i === nextLowestIndex) return "#1976d2"; // blue for lowest
          return "rgb(54, 162, 235)";
        }),
        pointBorderColor: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return "transparent";
          if (i === nextHighestIndex) return "#388e3c";
          if (i === nextLowestIndex) return "#0d47a1";
          return "#fff";
        }),
        pointBorderWidth: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0;
          if (i === nextHighestIndex || i === nextLowestIndex) return 3;
          return 2;
        }),
        spanGaps: true,
      },
      // Future data (blue, with water shade) - feet
      {
        label: "Future Tide Height (ft)",
        data: timestamps.map((_, i) =>
          i >= firstFutureIndex ? tideValuesFeet[i] : null
        ),
        borderColor: "rgb(54, 162, 235)",
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(
            0,
            0,
            0,
            context.chart.height
          );
          gradient.addColorStop(0, "rgba(54, 162, 235, 0.25)");
          gradient.addColorStop(1, "rgba(54, 162, 235, 0.05)");
          return gradient;
        },
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        yAxisID: "y",
        pointRadius: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0; // No points for past data
          if (i === nextHighestIndex || i === nextLowestIndex) return 7;
          return 2;
        }),
        pointHoverRadius: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0;
          if (i === nextHighestIndex || i === nextLowestIndex) return 10;
          return 6;
        }),
        pointBackgroundColor: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return "transparent";
          if (i === nextHighestIndex) return "#43a047"; // green for highest
          if (i === nextLowestIndex) return "#1976d2"; // blue for lowest
          return "rgb(54, 162, 235)";
        }),
        pointBorderColor: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return "transparent";
          if (i === nextHighestIndex) return "#388e3c";
          if (i === nextLowestIndex) return "#0d47a1";
          return "#fff";
        }),
        pointBorderWidth: timestamps.map((_, i) => {
          if (i < firstFutureIndex) return 0;
          if (i === nextHighestIndex || i === nextLowestIndex) return 3;
          return 2;
        }),
        spanGaps: true,
      },
      // Invisible meters dataset for right y-axis
      {
        label: "Tide Height (m)",
        data: tideValues,
        borderColor: "transparent",
        backgroundColor: "transparent",
        borderWidth: 0,
        fill: false,
        tension: 0.4,
        yAxisID: "y1",
        pointRadius: 0,
        pointHoverRadius: 0,
        spanGaps: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: window.innerWidth < 768 ? 5 : 15,
        right: window.innerWidth < 768 ? 5 : 15,
        top: window.innerWidth < 768 ? 0 : 0,
        bottom: window.innerWidth < 768 ? 0 : 0,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      tooltip: {
        titleFont: {
          family:
            '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
        },
        bodyFont: {
          family:
            '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
        },
        callbacks: {
          title: function (context) {
            const timestamp = context[0].parsed.x;
            const date = DateTime.fromMillis(timestamp);
            return date.toFormat("MMM dd, yyyy HH:mm");
          },
          label: function (context) {
            // Only show tooltip for the first dataset (feet data)
            if (context.datasetIndex > 1) return null;

            const value = context.parsed.y;
            if (value === null) return null;
            const valueInMeters = value / 3.28084;
            return `Tide Height: ${value.toFixed(
              2
            )} ft (${valueInMeters.toFixed(2)} m)`;
          },
        },
      },
      legend: {
        display: false, // Hide legend since we only have one dataset
      },
      title: {
        display: true,
        text: "Tide Predictions",
        font: {
          size: window.innerWidth < 768 ? 14 : 16,
          weight: "bold",
          family:
            '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
        },
      },
      zoom: {
        pan: {
          enabled: true, // Re-enable pan for direct chart scrolling
          mode: "x",
        },
        zoom: {
          wheel: {
            enabled: true, // Re-enable zoom for direct chart scrolling
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
        limits: {
          x: {
            min: Math.min(...timestamps),
            max: Math.max(...timestamps),
            minRange: 60 * 60 * 1000 * 12, // 12 hours minimum
          },
          y: {
            min: minTideFeet - tideRangeFeet * 0.1,
            max: maxTideFeet + tideRangeFeet * 0.1,
          },
        },
      },
    },
    scales: {
      x: {
        type: "time",
        time: {
          unit: "hour",
          displayFormats: {
            hour: "HH:mm",
          },
          tooltipFormat: "MMM dd, yyyy HH:mm",
        },
        ticks: {
          source: "auto",
          autoSkip: true,
          maxTicksLimit: window.innerWidth < 768 ? 6 : 12,
          maxRotation: 0,
          font: {
            size: window.innerWidth < 768 ? 8 : 10,
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
          callback: function (value, index, ticks) {
            const date = DateTime.fromMillis(value);
            return [date.toFormat("LLL dd"), date.toFormat("HH:mm")];
          },
        },
        grid: {
          display: true,
        },
        border: {
          display: true,
        },
        // Set visible range based on scroll position
        min: visibleStart,
        max: visibleEnd,
      },
      y: {
        type: "linear",
        display: true,
        position: "left",
        title: {
          display: true,
          text: "Tide Height (ft)",
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10,
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
          callback: function (value) {
            return `${value.toFixed(2)} ft`;
          },
          stepSize: 1.5,
        },
        suggestedMin: minTideFeet - tideRangeFeet * 0.1,
        suggestedMax: maxTideFeet + tideRangeFeet * 0.1,
      },
      y1: {
        type: "linear",
        display: true,
        position: "right",
        title: {
          display: true,
          text: "Tide Height (m)",
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
        },
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10,
            family:
              '"Noto Sans Canadian Aboriginal Variable", "Noto Sans Canadian Aboriginal", sans-serif',
          },
          callback: function (value) {
            return `${value.toFixed(2)} m`;
          },
        },
        suggestedMin: minTide - tideRange * 0.1,
        suggestedMax: maxTide + tideRange * 0.1,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={1}
        sx={{
          pl: { xs: -1, sm: 0 },
          pr: { xs: 1, sm: 2 },
          pt: { xs: 2, sm: 2 },
          pb: { xs: 2, sm: 2 },
        }}
      >
        {/* Highlighted summary at the top */}
        {(nextMaxTideFeet !== null || nextMinTideFeet !== null) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: window.innerWidth < 768 ? "column" : "row",
              justifyContent: "center",
              alignItems: window.innerWidth < 768 ? "center" : "center",
              gap: window.innerWidth < 768 ? 1 : 2,
              mb: 1,
              px: 2,
              py: 1,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              mx: 2,
            }}
          >
            {nextMaxTideFeet !== null && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  alignItems: window.innerWidth < 768 ? "center" : "center",
                  gap: window.innerWidth < 768 ? 0.5 : 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: "#43a047",
                      borderRadius: "50%",
                      display: "inline-block",
                      mr: 1,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: window.innerWidth < 768 ? "0.875rem" : "1rem",
                    }}
                  >
                    Next High Tide:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#43a047",
                      fontSize: window.innerWidth < 768 ? "0.875rem" : "1rem",
                    }}
                  >
                    {nextMaxTideFeet.toFixed(2)} ft ({nextMaxTide.toFixed(2)} m)
                  </Typography>
                </Box>
                {window.innerWidth < 768 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      textAlign: "center",
                    }}
                  >
                    On{" "}
                    {formatTime(
                      tideData.data[nextHighestIndex]?.PredictionTime
                    )}
                  </Typography>
                )}
                {window.innerWidth >= 768 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    ({" "}
                    {formatTime(
                      tideData.data[nextHighestIndex]?.PredictionTime
                    )}{" "}
                    )
                  </Typography>
                )}
              </Box>
            )}
            {nextMinTideFeet !== null && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: window.innerWidth < 768 ? "column" : "row",
                  alignItems: window.innerWidth < 768 ? "center" : "center",
                  gap: window.innerWidth < 768 ? 0.5 : 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: "#1976d2",
                      borderRadius: "50%",
                      display: "inline-block",
                      mr: 1,
                    }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: window.innerWidth < 768 ? "0.875rem" : "1rem",
                    }}
                  >
                    Next Low Tide:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#1976d2",
                      fontSize: window.innerWidth < 768 ? "0.875rem" : "1rem",
                    }}
                  >
                    {nextMinTideFeet.toFixed(2)} ft ({nextMinTide.toFixed(2)} m)
                  </Typography>
                </Box>
                {window.innerWidth < 768 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      textAlign: "center",
                    }}
                  >
                    On{" "}
                    {formatTime(tideData.data[nextLowestIndex]?.PredictionTime)}
                  </Typography>
                )}
                {window.innerWidth >= 768 && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                  >
                    ({" "}
                    {formatTime(tideData.data[nextLowestIndex]?.PredictionTime)}{" "}
                    )
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ height: "300px", position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={() => setIsFullscreen(true)}
              size="small"
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                },
              }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Box>

          <Line ref={chartRef} data={chartData} options={chartOptions} />
        </Box>

        {/* Custom Scrollbar */}
        {maxEndValue > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              mt: 1,
              px: { xs: 2, sm: 4 },
              py: { xs: 1.5, sm: 1 },
              borderRadius: 1,
            }}
          >
            {/* Desktop Layout */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              {/* Left Navigation Button */}
              <IconButton
                onClick={handleScrollLeft}
                disabled={dateRange[0] <= 0}
                size="small"
                sx={{
                  bgcolor: "white",
                  border: "1px solid #ddd",
                  "&:hover": { bgcolor: "grey.100" },
                  "&:disabled": { bgcolor: "grey.100", opacity: 0.5 },
                  minWidth: "32px",
                  minHeight: "32px",
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              {/* Desktop Range Slider */}
              <Box sx={{ flex: 1, mx: 1 }}>
                <Slider
                  value={dateRange}
                  onChange={handleRangeChange}
                  min={0}
                  max={maxEndValue}
                  step={0.5}
                  size="small"
                  marks={Array.from(
                    { length: Math.min(6, Math.ceil(totalDays)) },
                    (_, i) => {
                      const markValue =
                        (i * totalDays) / Math.min(6, Math.ceil(totalDays));
                      const markDate = new Date(
                        startDate.getTime() + markValue * 24 * 60 * 60 * 1000
                      );
                      return {
                        value: markValue,
                        label: DateTime.fromJSDate(markDate).toFormat("MMM dd"),
                      };
                    }
                  )}
                  sx={{
                    "& .MuiSlider-track": {
                      bgcolor: "primary.main",
                    },
                    "& .MuiSlider-rail": {
                      bgcolor: "grey.300",
                    },
                    "& .MuiSlider-thumb": {
                      bgcolor: "primary.main",
                      width: 14,
                      height: 14,
                      "&:hover": {
                        boxShadow: "0 0 0 4px rgba(25, 118, 210, 0.16)",
                      },
                    },
                    "& .MuiSlider-mark": {
                      bgcolor: "grey.400",
                    },
                    "& .MuiSlider-markLabel": {
                      fontSize: "0.6rem",
                      color: "text.secondary",
                    },
                  }}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => {
                    const date = new Date(
                      startDate.getTime() + value * 24 * 60 * 60 * 1000
                    );
                    return DateTime.fromJSDate(date).toFormat("MMM dd");
                  }}
                />
              </Box>

              {/* Right Navigation Button */}
              <IconButton
                onClick={handleScrollRight}
                disabled={dateRange[1] >= maxEndValue}
                size="small"
                sx={{
                  bgcolor: "white",
                  border: "1px solid #ddd",
                  "&:hover": { bgcolor: "grey.100" },
                  "&:disabled": { bgcolor: "grey.100", opacity: 0.5 },
                  minWidth: "32px",
                  minHeight: "32px",
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>

              {/* Desktop Date Range Display */}
              <Box sx={{ minWidth: "140px", textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                >
                  {DateTime.fromJSDate(visibleStart).toFormat("MMM dd")} -{" "}
                  {DateTime.fromJSDate(visibleEnd).toFormat("MMM dd")}
                </Typography>
              </Box>
            </Box>

            {/* Mobile Layout */}
            <Box
              sx={{
                display: { xs: "flex", sm: "none" },
                flexDirection: "column",
                width: "100%",
                gap: 1,
              }}
            >
              {/* Mobile Controls Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {/* Left Navigation Button */}
                <IconButton
                  onClick={handleScrollLeft}
                  disabled={dateRange[0] <= 0}
                  size="small"
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #ddd",
                    "&:hover": { bgcolor: "grey.100" },
                    "&:disabled": { bgcolor: "grey.100", opacity: 0.5 },
                    minWidth: "32px",
                    minHeight: "32px",
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                {/* Mobile Range Slider */}
                <Box sx={{ flex: 1, mx: 1.5 }}>
                  <Slider
                    value={dateRange}
                    onChange={handleRangeChange}
                    min={0}
                    max={maxEndValue}
                    step={0.5}
                    size="small"
                    marks={Array.from({ length: 6 }, (_, i) => {
                      const markValue = (i * totalDays) / 5; // 5 intervals for 6 marks
                      const markDate = new Date(
                        startDate.getTime() + markValue * 24 * 60 * 60 * 1000
                      );
                      return {
                        value: markValue,
                        label: DateTime.fromJSDate(markDate).toFormat("MMM dd"),
                      };
                    })}
                    sx={{
                      "& .MuiSlider-track": {
                        bgcolor: "primary.main",
                      },
                      "& .MuiSlider-rail": {
                        bgcolor: "grey.300",
                      },
                      "& .MuiSlider-thumb": {
                        bgcolor: "primary.main",
                        width: 16,
                        height: 16,
                        "&:hover": {
                          boxShadow: "0 0 0 4px rgba(25, 118, 210, 0.16)",
                        },
                        "&:active": {
                          boxShadow: "0 0 0 6px rgba(25, 118, 210, 0.24)",
                        },
                      },
                      "& .MuiSlider-mark": {
                        bgcolor: "grey.400",
                      },
                      "& .MuiSlider-markLabel": {
                        fontSize: "0.5rem",
                        color: "text.secondary",
                        fontWeight: 500,
                      },
                      "& .MuiSlider-root": {
                        padding: "6px 0",
                      },
                    }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => {
                      const date = new Date(
                        startDate.getTime() + value * 24 * 60 * 60 * 1000
                      );
                      return DateTime.fromJSDate(date).toFormat("MMM dd");
                    }}
                  />
                </Box>

                {/* Right Navigation Button */}
                <IconButton
                  onClick={handleScrollRight}
                  disabled={dateRange[1] >= maxEndValue}
                  size="small"
                  sx={{
                    bgcolor: "white",
                    border: "1px solid #ddd",
                    "&:hover": { bgcolor: "grey.100" },
                    "&:disabled": { bgcolor: "grey.100", opacity: 0.5 },
                    minWidth: "32px",
                    minHeight: "32px",
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Mobile Date Range Display */}
              <Box sx={{ textAlign: "center", px: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.65rem",
                    fontWeight: 500,
                  }}
                >
                  {DateTime.fromJSDate(visibleStart).toFormat("MMM dd, HH:mm")}{" "}
                  - {DateTime.fromJSDate(visibleEnd).toFormat("MMM dd, HH:mm")}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Fullscreen Modal */}
      <Modal open={isFullscreen} onClose={() => setIsFullscreen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            height: { xs: "80%", sm: "85%", md: "90%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 1,
          }}
        >
          <IconButton
            onClick={() => setIsFullscreen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ height: "100%", pt: 2 }}>
            <Line
              data={chartData}
              options={{
                ...chartOptions,
                plugins: {
                  ...chartOptions.plugins,
                  title: {
                    ...chartOptions.plugins.title,
                    text: "Tide Predictions (Fullscreen)",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default TideChart;
